import { Inject, Injectable, NgZone } from '@angular/core';
import { Data, NavigationExtras, ParamMap, Params, Router, UrlSerializer, convertToParamMap } from '@angular/router';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, from, map, merge, tap } from 'rxjs';
import { GotoError } from '../error';
import { ClearNotifications } from '../notification';
import { filterTrue } from '../rx-operators';
import { AutostartPageService } from './autostart-page.service';
import { SimpleRouterNavigationExtras } from './model/simple-router-navigation-extras';
import { SimpleRouterStateModel } from './model/simple-router-state-model';
import { RouterExtensionsOptions } from './router-extensions-options';
import { RouterExtensionsOptionsToken } from './router-extensions-options-token';
import { RouterStateUtilityService } from './router-state-utility.service';
import { Navigate, NavigateByUrl, SetGuardsActivated, SetSimpleRouterState } from './simple-router.actions';

const DEFAULT_STATE: SimpleRouterStateModel = {
  url: [],
  data: {},
  queryParamMap: convertToParamMap({}),
  paramMap: convertToParamMap({}),
  guardsActivated: true
};

@State<SimpleRouterStateModel>({
  name: 'simpleRouter',
  defaults: DEFAULT_STATE
})
@Injectable()
export class SimpleRouterState {
  constructor(
    private autostartPageService: AutostartPageService,
    private ngZone: NgZone,
    private router: Router,
    @Inject(RouterExtensionsOptionsToken) private routerExtensionsOptions: RouterExtensionsOptions,
    private routerStateUtilityService: RouterStateUtilityService,
    private urlSerializer: UrlSerializer
  ) {}

  @Selector()
  static urlWithoutParams(state: SimpleRouterStateModel): string {
    return '/' + decodeURIComponent(state.url.join('/'));
  }

  @Selector()
  static data(state: SimpleRouterStateModel): Data {
    return state.data;
  }

  @Selector()
  static isAutoClosePage(state: SimpleRouterStateModel): boolean {
    return state.data['isAutoClosePage'];
  }

  @Selector()
  static paramMap(state: SimpleRouterStateModel): ParamMap {
    return state.paramMap;
  }

  @Selector()
  static queryParamMap(state: SimpleRouterStateModel): ParamMap {
    return state.queryParamMap;
  }

  @Selector()
  static guardsActivated(state: SimpleRouterStateModel): boolean {
    return state.guardsActivated;
  }

  @Action(SetSimpleRouterState)
  setSimpleRouterState(ctx: StateContext<SimpleRouterStateModel>, { snapshot }: SetSimpleRouterState): void {
    ctx.patchState({ ...this.routerStateUtilityService.getRouterStateEssentialsFromChilds(snapshot) });
  }

  @Action(SetGuardsActivated)
  setGuardsActivated(ctx: StateContext<SimpleRouterStateModel>, { guardsActivated }: SetGuardsActivated): void {
    ctx.patchState({ guardsActivated });
  }

  @Action(Navigate)
  navigate(ctx: StateContext<SimpleRouterStateModel>, action: Navigate): Observable<unknown> {
    return this.runNavigationPromise(
      ctx,
      (navExtras: NavigationExtras) => this.router.navigate(action.path, navExtras),
      (navExtras: NavigationExtras) => this.urlSerializer.serialize(this.router.createUrlTree(action.path, navExtras)),
      action.extras
    );
  }

  @Action(NavigateByUrl)
  navigateByUrl(ctx: StateContext<SimpleRouterStateModel>, action: NavigateByUrl): Observable<unknown> {
    return this.runNavigationPromise(
      ctx,
      (navExtras: NavigationExtras) => this.router.navigateByUrl(action.url, navExtras),
      () => action.url,
      action.extras
    );
  }

  private runNavigationPromise(
    ctx: StateContext<SimpleRouterStateModel>,
    navigationPromise: (navExtras: NavigationExtras) => Promise<boolean>,
    fullyQualifiedUrl: (navExtras: NavigationExtras) => string,
    extras?: SimpleRouterNavigationExtras
  ): Observable<unknown> {
    const navExtras = this.buildNavigationExtras(ctx, extras);
    const forceAutostartPageMarking = this.autostartPageService.isAutostartPage() && navExtras?.replaceUrl === true;
    return merge([
      from(
        this.ngZone.run(() => this.getRunNavigationPromiseInNgZone(ctx, navigationPromise, fullyQualifiedUrl, this.buildNavigationExtras(ctx, extras)))
      ).pipe(
        map(() => forceAutostartPageMarking || (extras?.autostartPage ?? false)),
        filterTrue(),
        tap(() => this.autostartPageService.markPageAsAutostartPage())
      ),
      extras?.clearNotifications !== false ? ctx.dispatch(new ClearNotifications()) : EMPTY
    ]);
  }

  private getRunNavigationPromiseInNgZone(
    ctx: StateContext<SimpleRouterStateModel>,
    navigationPromise: (navExtras: NavigationExtras) => Promise<boolean>,
    fullyQualifiedUrl: (navExtras: NavigationExtras) => string,
    navExtras: NavigationExtras
  ): Promise<boolean> {
    return navigationPromise(navExtras).catch(err => {
      if (err.name === 'ChunkLoadError' && (!navExtras.queryParams || navExtras.queryParams['errorType'] !== 'SERVER_UNREACHABLE')) {
        ctx.dispatch(
          new GotoError({ replaceUrl: navExtras.replaceUrl, queryParams: { referrer: fullyQualifiedUrl(navExtras), errorType: 'SERVER_UNREACHABLE' } })
        );
      }
      throw err;
    });
  }

  private buildNavigationExtras(ctx: StateContext<SimpleRouterStateModel>, extras?: SimpleRouterNavigationExtras): NavigationExtras {
    let queryParams: Params | null = extras?.queryParams ?? null;
    const replaceUrl: boolean = extras?.replaceUrl ?? ctx.getState().data['replaceUrl'] ?? false;
    if (this.routerExtensionsOptions.qualifyReplaceUrl && replaceUrl) {
      queryParams = { ...queryParams, replacedPreviousUrlAt: new Date().toISOString() };
    }
    return { ...extras, queryParams, replaceUrl };
  }
}
